import { useEffect, useRef, useState } from 'react';
import { Form, Input, Toast } from 'antd-mobile';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { loginMember, sendVerifyCode } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { appendRegisterContext, mergeRegisterContextSearch, overlayIfRegisterKeysStale, saveReferToStorage } from '../utils/registerContext';
import { getShopId } from '../utils/shopId';
import { readWsTokenFromSearchParams, resolveWsTokenForWsRoute, buildRegisterFromWsPath } from '../utils/wsToken';
import { isValidInternationalPhone } from '../utils/phone';
import CountryCodeSelect from '../components/CountryCodeSelect';
import logoImg from '../assets/Ui_Logo.png';
import frameBgImg from '../assets/Ui_Frame_Bg.png';
import btnImg from '../assets/Ui_Bt_1.png';
import './LoginPage.css';

const COUNTDOWN_SECONDS = 60;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countryCode, setCountryCode] = useState('+60');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verificationId, setVerificationId] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // URL 中出现的 referId / shop id 写入 localStorage（与 index 首屏捕获配合）
  useEffect(() => {
    const r = searchParams.get('referId');
    if (r) {
      saveReferToStorage(r);
    }
    getShopId(searchParams);
  }, [searchParams]);

  /** 可从 localStorage 补全地址栏 query，与用户看到的 ?id= 一致（仅当 id/referId 取值变化时 replace） */
  useEffect(() => {
    const next = overlayIfRegisterKeysStale(searchParams);
    if (next) {
      navigate({ pathname: '/login', search: next.toString() }, { replace: true });
    }
  }, [navigate, searchParams]);

  const handleSendCode = async () => {
    if (!isValidInternationalPhone(countryCode, phone)) {
      Toast.show({ content: 'Please enter a valid phone number' });
      return;
    }
    if (countdown > 0) {
      return;
    }
    setSending(true);
    try {
      // 统一使用 /send-code 接口，后端会根据配置自动选择验证方式：
      // - ycloud: 返回包含 id 的响应，需要保存 verificationId
      // - way1/way2: 返回 null，不需要 verificationId
      const response = await sendVerifyCode(phone, countryCode);
      // 保存 verificationId（仅在使用 ycloud 时会有 id）
      if (response && response.id) {
        setVerificationId(response.id);
      } else {
        // way1/way2 方式不需要 verificationId
        setVerificationId(null);
      }
      Toast.show({ icon: 'success', content: 'Verification code sent' });
      setCountdown(COUNTDOWN_SECONDS);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      Toast.show({ icon: 'fail', content: error.message || 'Failed to send code, please try again later' });
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!isValidInternationalPhone(countryCode, phone)) {
      Toast.show({ content: 'Please enter a valid phone number' });
      return;
    }
    if (!code) {
      Toast.show({ content: 'Please enter the verification code' });
      return;
    }

    setVerifying(true);
    try {
      // 允许直接输入验证码登录（用于测试万能验证码）
      // verificationId 是可选的，如果不提供则后端可能使用万能验证码验证
      const loginResult = await loginMember(
        phone,
        code,
        countryCode,
        verificationId  // 传递 verificationId 用于 YCloud 验证（如果存在）
      );

      // 后端返回格式：{ member: Member, token: string, workerId: number }
      const member = loginResult?.member || loginResult;
      const token = loginResult?.token;
      // workerId 可能在 loginResult 顶层，也可能在 member 对象中
      const workerId = loginResult?.workerId || member?.workerId;
      
      // 调试日志
      console.log('Login - loginResult:', loginResult);
      console.log('Login - workerId:', workerId, 'Type:', typeof workerId);

      // Check if member is null/empty - user hasn't registered yet
      if (!member || !member.id) {
        Toast.show({ icon: 'info', content: 'Please complete registration' });

        // Redirect to register page with country code, phone and code
        const extra = new URLSearchParams({
          countryCode,
          phone,
          code,
        });
        const params = appendRegisterContext(mergeRegisterContextSearch(searchParams), extra);
        const redirect = searchParams.get('redirect');
        if (redirect) {
          params.set('redirect', redirect);
        }
        const redirectUrl = `/register?${params.toString()}`;
        navigate(redirectUrl, { replace: true });
        return;
      }

      // 如果 workerId 不为空，则当前登录会员为员工
      // workerId 是数字类型，如果存在且大于0，则表示是员工
      const isWorker = workerId != null && workerId !== undefined && workerId !== '' && Number(workerId) > 0;
      console.log('Login - isWorker calculated:', isWorker, 'will set to:', isWorker ? 1 : 0);

      // 保存用户信息和 token（90天有效期）
      const shopRaw = member.shopId ?? member.shop_id;
      const userProfile = {
        id: member.id,
        phone: member.phone ?? phone,
        nickname: member.nickName || member.nickname || 'Tao-go Member',
        lastLoginTime: member.lastLoginTime,
        isWorker: isWorker ? 1 : 0, // 保持与原有格式一致，1 表示是员工
      };
      if (shopRaw !== null && shopRaw !== undefined && shopRaw !== '') {
        const sid = Number(shopRaw);
        if (Number.isFinite(sid)) {
          userProfile.shopId = sid;
        }
      }
      console.log('Login - userProfile to save:', userProfile);
      login(userProfile, token);
      Toast.show({ icon: 'success', content: 'Logged in successfully' });
      
      // 检查是否有重定向URL、待处理的签到码、待处理的员工二维码或待处理的Spring 2026二维码
      const redirect = searchParams.get('redirect');
      const pendingSignCode = localStorage.getItem('pending-sign-code');
      const pendingWorkerCode = localStorage.getItem('pending-worker-code');
      const pendingSpring2026Code = localStorage.getItem('pending-spring2026-code');
      
      if (redirect) {
        navigate(redirect, { replace: true });
      } else if (pendingSignCode) {
        // 如果有待处理的签到码，跳转到签到页面
        localStorage.removeItem('pending-sign-code');
        navigate(`/sign?code=${encodeURIComponent(pendingSignCode)}`, { replace: true });
      } else if (pendingWorkerCode) {
        // 如果有待处理的员工二维码，跳转到绑定页面
        localStorage.removeItem('pending-worker-code');
        navigate(`/worker?code=${encodeURIComponent(pendingWorkerCode)}`, { replace: true });
      } else if (pendingSpring2026Code) {
        // 如果有待处理的Spring 2026二维码，跳转到扫描页面
        localStorage.removeItem('pending-spring2026-code');
        navigate(`/spring2026?code=${encodeURIComponent(pendingSpring2026Code)}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      Toast.show({ icon: 'fail', content: error.message || 'Login failed, please try again later' });
    } finally {
      setVerifying(false);
    }
  };

  const sendCodeText = countdown > 0 ? `${countdown}s` : 'Send Code';
  const wsTokenInUrl = readWsTokenFromSearchParams(searchParams);
  const wsTokenForLinks = resolveWsTokenForWsRoute(searchParams);
  if (wsTokenInUrl) {
    return <Navigate to={`/loginfromws?wstoken=${encodeURIComponent(wsTokenInUrl)}`} replace />;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src={logoImg} alt="TaoGo Logo" />
        </div>
        <h1 className="login-title">TaoGo Mart - Customer Portal</h1>
        <p className="login-subtitle">Login / Sign Up by keying in your WhatsApp number!</p>
        <Form layout="vertical" footer={null}>
          <Form.Item label="Phone number">
            <div className="phone-input-row">
              <CountryCodeSelect compact value={countryCode} onChange={setCountryCode} />
              <Input
                type="tel"
                maxLength={15}
                value={phone}
                onChange={(value) => setPhone(value.replace(/\D/g, '').slice(0, 14))}
                placeholder="Enter phone number"
                clearable
                className="phone-input"
              />
            </div>
          </Form.Item>
          <Form.Item label="Verification Code">
            <div className="verification-code-row">
              <Input
                value={code}
                onChange={setCode}
                maxLength={6}
                placeholder="Enter verification code"
                className="verification-input"
              />
              <button
                type="button"
                className="send-code-btn"
                onClick={handleSendCode}
                disabled={countdown > 0 || sending}
              >
                {sending ? 'Sending...' : sendCodeText}
              </button>
            </div>
          </Form.Item>
        </Form>
        <button
          type="button"
          className="login-btn"
          onClick={handleSubmit}
          disabled={verifying}
        >
          {verifying ? 'Logging in...' : 'Login'}
        </button>
        <div className="register-link">
          New user?{' '}
          <a
            href={wsTokenForLinks ? buildRegisterFromWsPath(wsTokenForLinks) : '/register'}
            onClick={(e) => {
              e.preventDefault();
              if (wsTokenForLinks) {
                navigate(buildRegisterFromWsPath(wsTokenForLinks), { replace: false });
                return;
              }
              const ctx = appendRegisterContext(
                mergeRegisterContextSearch(searchParams),
                new URLSearchParams(),
              );
              const rd = searchParams.get('redirect');
              if (rd) {
                ctx.set('redirect', rd);
              }
              navigate({ pathname: '/register', search: ctx.toString() });
            }}
          >
            Register here
          </a>
        </div>
      </div>
    </div>
  );
}
